import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdDone, MdLightbulbOutline, MdContentCopy } from "react-icons/md";
import { ActionIcon, Drawer, Flex, Group, Tabs, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { SpeechConfig } from "../../api";
import { TextToSpeech } from "./TextToSpeech";
import { DevelopmentSupportData } from "../types";
import { ThoughtProcess } from "../thought-process";
import { SupportingContent } from "../supporting-content";
import { Feedback } from "../feedback";
import { useAuthorization } from "../../AuthorizationContext";

export interface ResponseControlsProps {
    conversationId?: string;
    index?: number;
    response?: string;
    speechConfig: SpeechConfig;
    developmentSupportData?: DevelopmentSupportData;
    showFeedback: boolean;
}

export const ResponseControls = ({ response, index, speechConfig, developmentSupportData, showFeedback, conversationId }: ResponseControlsProps) => {
    const [developmentHelpOpened, { open: openDevelopmentHelp, close: closeDevelopmentHelp }] = useDisclosure(false);
    const [copied, setCopied] = useState(false);
    const { isAdmin } = useAuthorization();

    const { t } = useTranslation();

    const handleCopy = () => {
        if (!response) {
            return;
        }
        navigator.clipboard
            .writeText(response)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(err => console.error("Failed to copy text: ", err));
    };

    const onDevelopmentSupportClick = () => {
        openDevelopmentHelp();
    };

    return (
        <>
            <Flex justify="space-between" align="center" direction="row">
                <Group>
                    {isAdmin && developmentSupportData && onDevelopmentSupportClick && (
                        <ActionIcon variant="transparent" color="blue.9" onClick={onDevelopmentSupportClick}>
                            <MdLightbulbOutline size={24} />
                        </ActionIcon>
                    )}
                    <ActionIcon variant="transparent" color="blue.9" onClick={handleCopy}>
                        {copied ? <MdDone size={24} /> : <MdContentCopy size={20} />}
                    </ActionIcon>
                    {response && <TextToSpeech speechConfig={speechConfig} response={response} index={index} />}
                    {showFeedback && <Feedback conversationId={conversationId} turnIndex={index ?? 0} />}
                </Group>
            </Flex>
            <Text c="gray.5" fs="italic">
                {t("aiGeneratedResponse")}
            </Text>
            <Drawer withOverlay={true} position="right" size="xl" offset={8} radius="md" opened={developmentHelpOpened} onClose={closeDevelopmentHelp}>
                {developmentSupportData && (
                    <Tabs defaultValue="thoughtProcess">
                        <Tabs.List>
                            <Tabs.Tab value="thoughtProcess">{t("headerTexts.thoughtProcess")}</Tabs.Tab>
                            <Tabs.Tab value="supportingContent">{t("headerTexts.supportingContent")}</Tabs.Tab>
                        </Tabs.List>

                        <Tabs.Panel value="thoughtProcess" py="sm">
                            <ThoughtProcess data={developmentSupportData.thoughtProcessData} />
                        </Tabs.Panel>
                        <Tabs.Panel value="supportingContent">
                            <SupportingContent developmentSupportData={developmentSupportData} />
                        </Tabs.Panel>
                    </Tabs>
                )}
            </Drawer>
        </>
    );
};
